import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { EnfafDatetimePickerDarkComponent } from '../../../../components/enfaf-datetime-picker-dark/enfaf-datetime-picker-dark.component';
import { EnfafExpansionPanelLightComponent } from '../../../../components/enfaf-expansion-panel-light/enfaf-expansion-panel-light.component';
import { EnfafImgDraggerComponent } from '../../../../components/enfaf-img-dragger/enfaf-img-dragger.component';
import { EnfafInputComponent } from '../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafSelectLightComponent } from '../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EnfafSelectOption } from '../../../../components/enfaf-select/enfaf-select.component';
import { EnfafSwitchDarkComponent } from '../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { ApiService } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { CreatePromotionDto, PromotionDto, SegmentationValuesType } from '../../../../models/dto/promotion.dto';
import { PromotionPopupDelaySecondsType } from '../../../../models/enums/promotions/promotion-popup-delay-seconds-type.enum';
import { PromotionPopupShowAtType } from '../../../../models/enums/promotions/promotion-popup-show-at-type.enum';
import { PromotionSegmentationType } from '../../../../models/enums/promotions/promotion_segmentation_type.enum';
import { convertDateToISOString } from '../../../../shared/utils/convert-date-to-iso-string.util';
import { permissiveUrlValidator } from '../../../../shared/validators/permisive-url.validator';
import { PromotionManagementComponent } from '../promotion-management';
import { PROMOTION_SEGMENTATION_TYPE_OPTIONS } from './promotion-form.constants';
import { PromotionSegmentationTypeOption } from './promotion-form.models';
import { buttonTextErrors, buttonUrlErrors, endDateErrors, startDateErrors, subtitleErrors, titleErrors } from './promotions-form.errors';
import { endDateValidator, startDateValidator } from './promotions-form.validators';

@Component({
  selector: 'app-promotions-form',
  imports: [
    CommonModule,
    EnfafModalComponent,
    EnfafImgDraggerComponent,
    ReactiveFormsModule,
    EnfafInputComponent,
    MatProgressSpinner,
    MatAccordion,
    EnfafExpansionPanelLightComponent,
    EnfafDatetimePickerDarkComponent,
    PromotionManagementComponent,
    EnfafSwitchDarkComponent,
    EnfafSelectLightComponent,
    MatTooltipModule,
    MatIconModule
  ],
  templateUrl: './promotions-form.component.html',
  styleUrl: './promotions-form.component.scss'
})
export class PromotionsFormComponent implements OnInit, OnDestroy {
  @ViewChild(PromotionManagementComponent) promotionManagement?: PromotionManagementComponent;

  isSavingPromotion = false;
  isLoadingPromotion = true;

  promotionForm = this.fb.group({
    promotionImageUrl: this.fb.control<string | null>(null, []),
    mobileImageUrl: this.fb.control<string | null>(null, []),
    popupImageUrl: this.fb.control<string | null>(null, []),
    title: this.fb.control<string | null>(null, [Validators.required, Validators.minLength(3), Validators.maxLength(100)]),
    subtitle: this.fb.control<string | null>(null, [Validators.required, Validators.minLength(3), Validators.maxLength(255)]),
    button: this.fb.group({
      text: this.fb.control<string | null>(null, [Validators.minLength(1), Validators.maxLength(50)]),
      url: this.fb.control<string | null>(null, [Validators.required, permissiveUrlValidator])
    }),
    startDate: this.fb.control<Date | null>(new Date(), [Validators.required, startDateValidator]),
    endDate: this.fb.control<Date | null>(null, [endDateValidator]),
    hasCustomMobileImage: this.fb.control<boolean>(false, []),
    segmentationType: this.fb.control<PromotionSegmentationType>(PromotionSegmentationType.ALL_STUDENTS, [Validators.required]),
    isPopupEnabled: this.fb.control<boolean>(false, []),
    popup: this.fb.group({
      showAt: this.fb.control<PromotionPopupShowAtType>(PromotionPopupShowAtType.PLATFORM_ACCESS, [Validators.required]),
      delaySeconds: this.fb.control<PromotionPopupDelaySecondsType>(PromotionPopupDelaySecondsType.FIVETEEN_SECONDS, [Validators.required])
    })
  });

  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;
  mobileImageToUpload: File | null = null;
  mobileImageToUploadSrc: string | null = null;
  popupImageToUpload: File | null = null;
  popupImageToUploadSrc: string | null = null;

  promotion: PromotionDto | null = null;
  selectShowAtOptions: EnfafSelectOption[] = Object.values(PromotionPopupShowAtType).map((value) => ({
    value,
    viewValue: value
  }));
  selectDelaySecondsOptions: EnfafSelectOption[] = Object.values(PromotionPopupDelaySecondsType)
    .filter((value) => typeof value === 'number')
    .map((value) => ({
      value: value,
      viewValue: `${value} seconds`
    }));

  segmentationTypeOptions: PromotionSegmentationTypeOption[] = PROMOTION_SEGMENTATION_TYPE_OPTIONS;

  get titleErrors() {
    return titleErrors(this.promotionForm.get('title') as FormControl);
  }
  get subtitleErrors() {
    return subtitleErrors(this.promotionForm.get('subtitle') as FormControl);
  }
  get buttonUrlErrors() {
    return buttonUrlErrors(this.promotionForm.get('button.url') as FormControl);
  }
  get buttonTextErrors() {
    return buttonTextErrors(this.promotionForm.get('button.text') as FormControl);
  }
  get startDateErrors() {
    return startDateErrors(this.promotionForm.get('startDate') as FormControl);
  }
  get endDateErrors() {
    return endDateErrors(this.promotionForm.get('endDate') as FormControl);
  }

  get buttonFormGroup() {
    return this.promotionForm.get('button') as FormGroup;
  }

  get startDateFormControl() {
    return this.promotionForm.get('startDate') as FormControl;
  }

  get endDateFormControl() {
    return this.promotionForm.get('endDate') as FormControl;
  }

  get hasCustomMobileImageControl() {
    return this.promotionForm.get('hasCustomMobileImage') as FormControl;
  }

  get isPopupEnabledControl() {
    return this.promotionForm.get('isPopupEnabled') as FormControl;
  }

  get popupFormGroup() {
    return this.promotionForm.get('popup') as FormGroup;
  }

  get hasMobileImage(): boolean {
    return !!(this.promotionForm.value.mobileImageUrl || this.mobileImageToUploadSrc);
  }

  get hasPopupImage(): boolean {
    return !!(this.promotionForm.value.popupImageUrl || this.popupImageToUploadSrc);
  }

  showImageInfoModal: boolean = false;
  showPopupAbove: boolean = false;

  private _destroyed = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { promotionId?: string },
    private dialogRef: MatDialogRef<PromotionsFormComponent>,
    private fb: FormBuilder,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupDateValidationListeners();
    this.setupButtonUrlValidation();
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  onMouseEnterTooltip(event: MouseEvent): void {
    this.showImageInfoModal = true;

    // Calcular si hay espacio debajo
    const element = event.currentTarget as HTMLElement;
    const rect = element.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const popupHeight = 400; // Altura estimada del popup

    // Si no hay suficiente espacio debajo, mostrar arriba
    this.showPopupAbove = spaceBelow < popupHeight;
  }

  onMouseLeaveTooltip(): void {
    this.showImageInfoModal = false;
    this.showPopupAbove = false;
  }

  private setupDateValidationListeners(): void {
    // When startDate changes, re-validate endDate
    this.promotionForm.get('startDate')?.valueChanges.subscribe(() => {
      const endDateControl = this.promotionForm.get('endDate');
      if (endDateControl) {
        endDateControl.updateValueAndValidity({ emitEvent: false });
        // Mark as touched if it has a value so errors are shown
        if (endDateControl.value) {
          endDateControl.markAsTouched();
        }
      }
    });

    // When endDate changes, re-validate startDate
    this.promotionForm.get('endDate')?.valueChanges.subscribe(() => {
      const startDateControl = this.promotionForm.get('startDate');
      if (startDateControl) {
        startDateControl.updateValueAndValidity({ emitEvent: false });
        // Mark as touched so errors are shown
        startDateControl.markAsTouched();
      }
    });
  }

  private setupButtonUrlValidation(): void {
    // When promotionImageUrl changes, update button.url validators
    this.promotionForm
      .get('promotionImageUrl')
      ?.valueChanges.pipe(takeUntil(this._destroyed))
      .subscribe(() => {
        this.updateButtonUrlValidators();
      });
  }

  private updateButtonUrlValidators(): void {
    const buttonUrlControl = this.promotionForm.get('button.url');
    const promotionImageUrl = this.promotionForm.get('promotionImageUrl')?.value;

    if (buttonUrlControl) {
      if (promotionImageUrl || this.imageToUploadSrc) {
        // Image uploaded: button.url is required
        buttonUrlControl.setValidators([Validators.required, permissiveUrlValidator]);
      } else {
        // No image: button.url is optional
        buttonUrlControl.setValidators([permissiveUrlValidator]);
      }
      buttonUrlControl.updateValueAndValidity();
    }
  }

  async initForm(): Promise<void> {
    try {
      const promotionId = (this.data && this.data.promotionId) || null;

      if (promotionId) {
        const response = await firstValueFrom(this.api.fetchPromotionById(promotionId));
        if (response && response.promotion) {
          this.promotion = response.promotion;
        }
      }

      if (this.promotion) {
        this.promotionForm.patchValue({
          promotionImageUrl: this.promotion?.promotionImageUrl || null,
          mobileImageUrl: this.promotion?.mobileImageUrl || null,
          popupImageUrl: this.promotion?.popupImageUrl || null,
          title: this.promotion?.title || null,
          subtitle: this.promotion?.subtitle || null,
          button: {
            text: this.promotion?.button?.text || null,
            url: this.promotion?.button?.url || null
          },
          startDate: this.promotion?.startDate ? new Date(this.promotion.startDate) : new Date(),
          endDate: this.promotion?.endDate ? new Date(this.promotion.endDate) : null,
          segmentationType: (this.promotion as any)?.segmentationType || PromotionSegmentationType.ALL_STUDENTS,
          hasCustomMobileImage: !!this.promotion?.mobileImageUrl,
          isPopupEnabled: this.promotion?.isPopupEnabled ?? false,
          popup: {
            showAt: this.promotion?.popup?.showAt || PromotionPopupShowAtType.PLATFORM_ACCESS,
            delaySeconds: this.promotion?.popup?.delaySeconds || PromotionPopupDelaySecondsType.FIVETEEN_SECONDS
          }
        });
        // The segmentation loading is done automatically with the @Input promotion
        // if (this.promotionManagement) {
        //   await this.promotionManagement.loadPromotionSegmentation(this.promotion!);
        // }
      }
      this.isLoadingPromotion = false;
    } catch (error) {
      this.isLoadingPromotion = false;
      this.promotion = null;
      this.toast.presentErrorToast('There was an error fetching the promotion');
    }
  }

  uploadImage(data: { imageToUpload: File | null; imageToUploadSrc: string | null }): void {
    this.imageToUpload = data.imageToUpload;
    this.imageToUploadSrc = data.imageToUploadSrc;
    this.updateButtonUrlValidators();
  }

  deleteImage(): void {
    this.imageToUpload = null;
    this.imageToUploadSrc = null;
    this.promotionForm.patchValue({
      promotionImageUrl: null
    });
    this.promotionForm.get('promotionImageUrl')?.updateValueAndValidity();
    this.updateButtonUrlValidators();
  }

  uploadMobileImage(data: { imageToUpload: File | null; imageToUploadSrc: string | null }): void {
    this.mobileImageToUpload = data.imageToUpload;
    this.mobileImageToUploadSrc = data.imageToUploadSrc;
  }

  deleteMobileImage(): void {
    this.mobileImageToUpload = null;
    this.mobileImageToUploadSrc = null;
    this.promotionForm.patchValue({
      mobileImageUrl: null
    });
    this.promotionForm.get('mobileImageUrl')?.updateValueAndValidity();
  }

  uploadPopupImage(data: { imageToUpload: File | null; imageToUploadSrc: string | null }): void {
    this.popupImageToUpload = data.imageToUpload;
    this.popupImageToUploadSrc = data.imageToUploadSrc;
  }

  deletePopupImage(): void {
    this.popupImageToUpload = null;
    this.popupImageToUploadSrc = null;
    this.promotionForm.patchValue({
      popupImageUrl: null
    });
    this.promotionForm.get('popupImageUrl')?.updateValueAndValidity();
  }

  handleCancel() {
    this.promotionForm.reset();
    this.dialogRef.close({
      result: false,
      data: null
    });
  }

  private mapSegmentationToDto(segmentationType: PromotionSegmentationType): SegmentationValuesType {
    if (segmentationType === PromotionSegmentationType.ALL_STUDENTS) {
      return { segmentationType };
    } else if (segmentationType === PromotionSegmentationType.SPECIFIC_STUDENTS) {
      const studentSegmentations =
        this.promotionManagement?.selectedStudents.map((student) => ({
          userId: student.id
        })) || [];

      return {
        segmentationType,
        studentSegmentations
      };
    } else {
      const courseSegmentations: Array<{ courseId: string; mustBeEnrolled: boolean }> = [];

      if (this.promotionManagement?.selectedEnrolledCourses) {
        this.promotionManagement.selectedEnrolledCourses.forEach((course) => {
          courseSegmentations.push({
            courseId: course.id,
            mustBeEnrolled: true
          });
        });
      }

      if (this.promotionManagement?.selectedNotEnrolledCourses) {
        this.promotionManagement.selectedNotEnrolledCourses.forEach((course) => {
          courseSegmentations.push({
            courseId: course.id,
            mustBeEnrolled: false
          });
        });
      }

      return {
        segmentationType,
        courseSegmentations
      };
    }
  }

  async handleSave() {
    try {
      this.isSavingPromotion = true;
      if (this.promotionForm.invalid) {
        this.isSavingPromotion = false;
        this.promotionForm.markAllAsTouched();
        return;
      }
      const formValue = this.promotionForm.value;

      const courseFormData = new FormData();

      const body: CreatePromotionDto = {
        title: formValue.title!,
        subtitle: formValue.subtitle!,
        button: {
          text: formValue.button!.text ?? '',
          url: formValue.button!.url!
        },
        startDate: convertDateToISOString(formValue.startDate!),
        endDate: formValue.endDate ? convertDateToISOString(formValue.endDate) : undefined,
        isActive: true,
        isPopupEnabled: formValue.isPopupEnabled ?? false,
        promotionImageUrl: formValue.promotionImageUrl ?? null,
        mobileImageUrl: formValue.mobileImageUrl ?? null,
        popupImageUrl: formValue.popupImageUrl ?? null,
        ...this.mapSegmentationToDto(formValue.segmentationType!)
      };
      if (formValue.isPopupEnabled) {
        body.popup = {
          showAt: formValue.popup!.showAt!,
          delaySeconds: formValue.popup!.delaySeconds!
        };
      }

      if (this.imageToUpload) {
        courseFormData.append('promotionImage', this.imageToUpload, this.imageToUpload.name);
      }

      if (this.mobileImageToUpload) {
        courseFormData.append('mobileImage', this.mobileImageToUpload, this.mobileImageToUpload.name);
      }

      if (this.popupImageToUpload) {
        courseFormData.append('popupImage', this.popupImageToUpload, this.popupImageToUpload.name);
      }

      let message = '';
      let promotion: PromotionDto | null = null;
      let success = false;

      if (this.promotion) {
        courseFormData.append(
          'form',
          JSON.stringify({
            ...body,
            promotionId: this.promotion.promotionId
          })
        );
        const response = await firstValueFrom(this.api.updatePromotion(courseFormData));
        message = response.message;
        promotion = response.promotion;
        success = response.success;
      } else {
        courseFormData.append('form', JSON.stringify(body));
        const response = await firstValueFrom(this.api.createPromotion(courseFormData));
        message = response.message;
        promotion = response.promotion;
        success = response.success;
      }

      this.isSavingPromotion = false;
      this.promotionForm.reset();
      this.toast.presentSuccessToast(message);

      this.dialogRef.close({
        result: success,
        data: promotion
      });
    } catch (error) {
      this.isSavingPromotion = false;
      this.toast.presentErrorToast('There was an error saving the promotion');
      return;
    }
  }
}
